import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, lastValueFrom, Observable } from 'rxjs';

import { getCookie } from '../tools/get-cookie';

import { ApplicationType } from 'shared/openapi/models/application-type';
import { CsrfTokenCookieName } from 'shared/openapi/models/csrf-token-cookie-name';
import { InternalAuthBaseService } from 'shared/services/internal-auth-base.service';

const CUSTOM_HEADER_PREFIX = 'X-';

export abstract class AuthorizationInterceptor implements HttpInterceptor {
	protected requiredHeaders: Record<string, string> = {};
	protected csrfTokenCookieName!: CsrfTokenCookieName;
	protected applicationType!: ApplicationType;

	protected constructor(protected authService: InternalAuthBaseService) {}

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const csrfToken = getCookie(this.csrfTokenCookieName);
		const csrfHeaderName = CUSTOM_HEADER_PREFIX + this.csrfTokenCookieName;

		if (csrfToken) {
			const req = request.clone({
				setHeaders: {
					[csrfHeaderName]: csrfToken,
					...this.requiredHeaders,
				},
			});
			return next.handle(req);
		} else {
			return from(this.handleAsync(request, next));
		}
	}

	protected async handleAsync(request: HttpRequest<unknown>, next: HttpHandler) {
		if (request.url.includes('public') && !request.url.includes('hello')) {
			const invokeHelloIfUserIsNotLoggedIn = this.invokeHelloIfUserIsNotLoggedIn();
			if (invokeHelloIfUserIsNotLoggedIn) {
				await lastValueFrom(invokeHelloIfUserIsNotLoggedIn);
				return this.handleHelloResponse(request, next);
			}
		}
		const requestWithoutCSRF = request.clone({
			setHeaders: this.requiredHeaders,
		});
		return lastValueFrom(next.handle(requestWithoutCSRF));
	}

	protected async handleHelloResponse(request: HttpRequest<unknown>, next: HttpHandler) {
		const csrfToken = getCookie(this.csrfTokenCookieName) || '';
		const csrfHeaderName = CUSTOM_HEADER_PREFIX + this.csrfTokenCookieName;
		const requestWithCSRF = request.clone({
			setHeaders: {
				[csrfHeaderName]: csrfToken,
				...this.requiredHeaders,
			},
		});
		return lastValueFrom(next.handle(requestWithCSRF));
	}

	protected invokeHelloIfUserIsNotLoggedIn(): Observable<void> | undefined {
		return this.authService.invokeHelloIfUserIsNotLoggedIn(this.csrfTokenCookieName, this.applicationType);
	}
}
